import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Box,
  Typography,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Link,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  TextField
} from '@mui/material';
import { Close, PictureAsPdf, Save } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';
import { useToast } from '../../contexts/ToastContext';
import StoreDocumentsDialog from './StoreDocumentsDialog';
import CertificateField from './CertificateField';
import {
  findInventoryCertificateDocument,
  downloadInventoryCertificate
} from '../../utils/storeCertificateDocuments';
import {
  formatPartMasterOptionLabel,
  formatInventorySerialNumbersDisplay,
  collectInventorySerialNumbers,
  getInventoryDisplayExpiryDate,
  getInventoryDisplayBatchNumber,
  isPlaceholderSerialNumber
} from '../../utils/partMasterLabel';
import { PART_CONDITION_LABELS } from '../../constants/partUnitConditions';
import { formatStoreTypeLabel, normalizeStoreTypeValue } from '../../constants/storeTypes';
import { unitMetricLabel } from '../../constants/unitMetrics';
import { INVENTORY_CATEGORY_LABELS } from '../../constants/inventoryCategories';
import { downloadAuthenticatedPdf } from '../../utils/authenticatedPdf';

function unitPoForItem(unit, item) {
  return String(unit?.purchaseOrderNumber || item?.purchaseOrderNumber || '').trim();
}

function unitPriForItem(unit) {
  return String(unit?.priNumber || '').trim();
}

function collectInventoryPoNumbers(item) {
  if (!item) return [];
  const pos = new Set();
  const itemPo = String(item.purchaseOrderNumber || '').trim();
  if (itemPo) pos.add(itemPo);
  (Array.isArray(item.unitDetails) ? item.unitDetails : []).forEach((unit) => {
    const po = unitPoForItem(unit, item);
    if (po) pos.add(po);
  });
  return Array.from(pos).sort();
}

function collectInventoryPriNumbers(item) {
  if (!item) return [];
  const pris = [];
  const seen = new Set();
  (Array.isArray(item.unitDetails) ? item.unitDetails : []).forEach((unit) => {
    const pri = unitPriForItem(unit);
    if (!pri || seen.has(pri)) return;
    seen.add(pri);
    pris.push(pri);
  });
  // Newest first: higher sequence / later date typically sorts lexicographically for same-day PRIs
  return pris.sort((a, b) => String(b).localeCompare(String(a)));
}

function collectSerialsForPurchaseOrder(item, purchaseOrderNumber) {
  const allSerials = item ? collectInventorySerialNumbers(item) : [];
  const po = String(purchaseOrderNumber || '').trim();
  if (!item || !po) return allSerials;

  const units = Array.isArray(item.unitDetails) ? item.unitDetails : [];
  if (units.length === 0) {
    const itemPo = String(item.purchaseOrderNumber || '').trim();
    return itemPo === po ? allSerials : [];
  }

  const seen = new Set();
  const serials = [];
  units.forEach((unit) => {
    if (unitPoForItem(unit, item) !== po) return;
    String(unit?.serialNumber || '')
      .split(',')
      .map((part) => part.trim())
      .filter((serial) => serial && !isPlaceholderSerialNumber(serial))
      .forEach((serial) => {
        if (seen.has(serial)) return;
        seen.add(serial);
        serials.push(serial);
      });
  });

  const itemPo = String(item.purchaseOrderNumber || '').trim();
  if (itemPo === po) {
    const unitSerialKeys = new Set(
      units
        .flatMap((unit) => String(unit?.serialNumber || '').split(','))
        .map((part) => part.trim().toLowerCase())
        .filter((serial) => serial && !isPlaceholderSerialNumber(serial))
    );
    allSerials.forEach((serial) => {
      if (isPlaceholderSerialNumber(serial)) return;
      if (unitSerialKeys.has(serial.toLowerCase()) || seen.has(serial)) return;
      seen.add(serial);
      serials.push(serial);
    });
  }

  return serials;
}

function collectSerialsForPri(item, priNumber) {
  const pri = String(priNumber || '').trim();
  if (!item || !pri) return [];
  const units = Array.isArray(item.unitDetails) ? item.unitDetails : [];
  const seen = new Set();
  const serials = [];
  units.forEach((unit) => {
    if (unitPriForItem(unit) !== pri) return;
    String(unit?.serialNumber || '')
      .split(',')
      .map((part) => part.trim())
      .filter((serial) => serial && !isPlaceholderSerialNumber(serial))
      .forEach((serial) => {
        if (seen.has(serial)) return;
        seen.add(serial);
        serials.push(serial);
      });
  });
  return serials;
}

function unitExpiryDayForTag(raw) {
  if (!raw) return '';
  const d = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/**
 * Distinct batch/lot (+ expiry) cohorts for Acceptance Tag download when PRIs are absent.
 * Same P/N with different batches or expiry dates each get their own tag.
 * Placeholder batches (N/A) are ignored. Returns [] when real serials apply (serial tags instead).
 */
function collectBatchesForTagDownload(item, purchaseOrderNumber) {
  if (!item) return [];
  const units = Array.isArray(item.unitDetails) ? item.unitDetails : [];
  const po = String(purchaseOrderNumber || '').trim();
  const scoped = units.filter((unit) => {
    if (!po) return true;
    return unitPoForItem(unit, item) === po;
  });
  if (scoped.some((unit) => !isPlaceholderSerialNumber(unit?.serialNumber))) {
    return [];
  }
  const seen = new Set();
  const cohorts = [];
  scoped.forEach((unit) => {
    const batch = String(unit?.batchNumber || '').trim();
    if (!batch || isPlaceholderSerialNumber(batch)) return;
    const expiryDate = unitExpiryDayForTag(unit?.expiryDate);
    const key = `${batch}|${expiryDate}`;
    if (seen.has(key)) return;
    seen.add(key);
    cohorts.push({ batchNumber: batch, expiryDate });
  });
  if (cohorts.length === 0) {
    const lineBatch = String(item.batchNumber || '').trim();
    if (lineBatch && !isPlaceholderSerialNumber(lineBatch)) {
      cohorts.push({
        batchNumber: lineBatch,
        expiryDate: unitExpiryDayForTag(item.expiryDate)
      });
    }
  }
  return cohorts;
}

const TAB_INDICES = { returns: 4, reconciliation: 5, loanItems: 10 };

const getTabForLink = (sourceType, sourceLabel, inventoryDomain) => {
  if (inventoryDomain === 'LOAN') {
    return TAB_INDICES.loanItems;
  }
  if (sourceLabel === 'Returns') return TAB_INDICES.returns;
  if (sourceType === 'reconciliation') return TAB_INDICES.reconciliation;
  return null;
};

const InventoryItemDetailModal = ({ itemId, open, onClose, inventoryDomain = 'STORE', onItemUpdated, operatorId = '' }) => {
  const { user } = useAuth();
  const storeApi = useCallback(
    (path) => `/api/store${inventoryDomain === 'LOAN' ? '/loan' : ''}${path}`,
    [inventoryDomain]
  );
  const [item, setItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingItem, setLoadingItem] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [documentsOpen, setDocumentsOpen] = useState(false);
  const [selectedPo, setSelectedPo] = useState('');
  const [selectedPri, setSelectedPri] = useState('');
  const [selectedSerial, setSelectedSerial] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingCertificate, setDownloadingCertificate] = useState(false);
  const [locationForm, setLocationForm] = useState({
    locationText: '',
    location: { warehouse: '', shelf: '', bin: '' }
  });
  const [savingLocation, setSavingLocation] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const canEditLocation = [ROLES.STORE_INSPECTOR, ROLES.STORE_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SUPER_ADMIN].includes(user?.role);

  const fetchItem = useCallback(async () => {
    if (!itemId) return;
    setLoadingItem(true);
    try {
      const res = await axios.get(storeApi(`/inventory/${itemId}`));
      setItem(res.data);
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || 'Failed to load item', 'error');
    } finally {
      setLoadingItem(false);
    }
  }, [itemId, showToast, storeApi]);

  const fetchHistory = useCallback(async () => {
    if (!itemId) return;
    setLoadingHistory(true);
    try {
      const res = await axios.get(storeApi(`/inventory/${itemId}/history`));
      setHistory(res.data || []);
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || 'Failed to load history', 'error');
    } finally {
      setLoadingHistory(false);
    }
  }, [itemId, showToast, storeApi]);

  useEffect(() => {
    if (open && itemId) {
      fetchItem();
      fetchHistory();
    }
  }, [open, itemId, fetchItem, fetchHistory]);


  useEffect(() => {
    if (!open) setTabValue(0);
  }, [open]);

  useEffect(() => {
    if (!item) {
      setSelectedPo('');
      setSelectedPri('');
      setSelectedSerial('');
      setLocationForm({ locationText: '', location: { warehouse: '', shelf: '', bin: '' } });
      return;
    }
    const pris = collectInventoryPriNumbers(item);
    const pos = collectInventoryPoNumbers(item);
    const nextPri = pris.length === 1 ? pris[0] : '';
    const nextPo = pos.length === 1 ? pos[0] : '';
    setSelectedPri(nextPri);
    setSelectedPo(pris.length > 0 ? '' : nextPo);
    const serials = pris.length > 0
      ? (nextPri ? collectSerialsForPri(item, nextPri) : [])
      : collectSerialsForPurchaseOrder(item, nextPo);
    setSelectedSerial(serials.length === 1 ? serials[0] : '');
    setLocationForm({
      locationText: item.locationText || '',
      location: {
        warehouse: item.location?.warehouse || '',
        shelf: item.location?.shelf || '',
        bin: item.location?.bin || ''
      }
    });
  }, [item]);

  const handleClose = () => {
    setItem(null);
    setHistory([]);
    onClose();
  };

  const handleOpenRecord = (sourceType, sourceId, sourceLabel) => {
    if (!sourceId) return;
    handleClose();
    if (sourceType === 'issuance' && inventoryDomain !== 'LOAN') {
      navigate('/issuance', { state: { recordType: sourceType, recordId: sourceId } });
      return;
    }
    if (sourceLabel === 'Returns' && inventoryDomain !== 'LOAN') {
      navigate('/store/returns', { state: { recordType: 'issuance', recordId: sourceId, stayOnStoreReturns: true } });
      return;
    }
    if (sourceType === 'receiving') {
      if (inventoryDomain === 'LOAN') {
        navigate('/loan', {
          state: { tab: TAB_INDICES.loanItems, loanTab: 1, recordType: 'receiving', recordId: sourceId }
        });
        return;
      }
      navigate('/receiving-and-inspection', {
        state: { recordType: 'receiving', recordId: sourceId, receivingType: 'AGAINST_PO' }
      });
      return;
    }
    const tab = getTabForLink(sourceType, sourceLabel, inventoryDomain);
    if (tab == null) return;
    const state =
      inventoryDomain === 'LOAN'
        ? { tab, loanTab: 2, recordType: sourceType, recordId: sourceId }
        : { tab, recordType: sourceType, recordId: sourceId };
    navigate('/store', { state });
  };

  const getLocationString = (loc) => {
    if (!loc) return '—';
    const parts = [loc.warehouse, loc.shelf, loc.bin].filter(Boolean);
    return parts.length ? parts.join(' / ') : '—';
  };

  const whoName = (who) => (who ? `${who.firstName || ''} ${who.lastName || ''}`.trim() || '—' : '—');
  const serialDisplay = item ? formatInventorySerialNumbersDisplay(item) : '';
  const priOptions = useMemo(() => collectInventoryPriNumbers(item), [item]);
  const poOptions = useMemo(() => collectInventoryPoNumbers(item), [item]);
  const usePriSelection = priOptions.length > 0;
  const serialsForSelection = useMemo(() => {
    if (!item) return [];
    if (usePriSelection) {
      if (priOptions.length > 1 && !selectedPri) return [];
      return collectSerialsForPri(item, selectedPri);
    }
    if (poOptions.length > 1 && !selectedPo) return [];
    return collectSerialsForPurchaseOrder(item, selectedPo);
  }, [item, usePriSelection, priOptions.length, selectedPri, poOptions.length, selectedPo]);
  const batchOptionsForTag = useMemo(() => {
    if (!item || usePriSelection) return [];
    if (poOptions.length > 1 && !selectedPo) return [];
    return collectBatchesForTagDownload(item, selectedPo);
  }, [item, usePriSelection, poOptions.length, selectedPo]);
  const displayExpiryDate = item ? getInventoryDisplayExpiryDate(item) : null;
  const displayBatchNumber = item ? getInventoryDisplayBatchNumber(item) : '';

  const unitPoNumber = (unit) => {
    const po = unitPoForItem(unit, item);
    return po || '—';
  };

  const handlePriChange = (pri) => {
    setSelectedPri(pri);
    const serials = collectSerialsForPri(item, pri);
    setSelectedSerial(serials.length === 1 ? serials[0] : '');
  };

  const handlePoChange = (po) => {
    setSelectedPo(po);
    const serials = collectSerialsForPurchaseOrder(item, po);
    setSelectedSerial(serials.length === 1 ? serials[0] : '');
  };

  const handleSaveLocation = async () => {
    if (!itemId || !canEditLocation) return;
    setSavingLocation(true);
    try {
      const res = await axios.patch(storeApi(`/inventory/${itemId}/location`), {
        locationText: locationForm.locationText,
        location: locationForm.location
      });
      setItem(res.data);
      showToast('Location updated', 'success');
      onItemUpdated?.(res.data);
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || 'Failed to update location', 'error');
    } finally {
      setSavingLocation(false);
    }
  };

  const certificateDoc = useMemo(
    () => (item ? findInventoryCertificateDocument(item.documents, item.partNumber) : null),
    [item]
  );

  const handleDownloadCertificate = async () => {
    if (!item?._id || !certificateDoc?._id || downloadingCertificate) return;
    setDownloadingCertificate(true);
    try {
      await downloadInventoryCertificate(item._id, certificateDoc, undefined, inventoryDomain);
    } catch (err) {
      showToast(err.message || err.response?.data?.message || 'Failed to download certificate', 'error');
    } finally {
      setDownloadingCertificate(false);
    }
  };

  const handleDownloadPartDetailPdf = async () => {
    if (!itemId) return;
    if (usePriSelection) {
      if (priOptions.length > 1 && !selectedPri && serialsForSelection.length > 0) {
        showToast('Select a PRI number to download the store acceptance tag', 'warning');
        return;
      }
    } else if (poOptions.length > 1 && !selectedPo) {
      showToast('Select a PO number to download the part detail PDF', 'warning');
      return;
    }
    // Serialized: one tag per serial. Multi-batch (no serials): one tag per PRI/batch qty.
    const priTargets = usePriSelection
      ? (selectedPri
        ? [selectedPri]
        : (serialsForSelection.length > 0 ? [] : priOptions))
      : [''];
    if (usePriSelection && priTargets.length === 0) {
      showToast('Select a PRI number to download the store acceptance tag', 'warning');
      return;
    }
    const serialTargets = (() => {
      if (selectedSerial && !isPlaceholderSerialNumber(selectedSerial)) {
        return [selectedSerial];
      }
      if (serialsForSelection.length > 0) return serialsForSelection;
      return [''];
    })();
    // No PRIs + multiple batch/expiry cohorts: one Acceptance Tag per cohort.
    const batchTargets = (!usePriSelection && batchOptionsForTag.length > 1 && serialTargets.length === 1 && !serialTargets[0])
      ? batchOptionsForTag
      : [{ batchNumber: '', expiryDate: '' }];
    setDownloadingPdf(true);
    try {
      let downloadCount = 0;
      for (const pri of priTargets) {
        for (const cohort of batchTargets) {
          const batch = cohort?.batchNumber || '';
          const expiryDate = cohort?.expiryDate || '';
          for (const serial of serialTargets) {
            const params = new URLSearchParams({ download: '1' });
            if (pri) params.set('priNumber', pri);
            else if (batch) {
              params.set('batchNumber', batch);
              if (expiryDate) params.set('expiryDate', expiryDate);
              if (selectedPo) params.set('purchaseOrderNumber', selectedPo);
            } else if (selectedPo) {
              params.set('purchaseOrderNumber', selectedPo);
            }
            if (serial) params.set('serialNumber', serial);
            if (operatorId) params.set('operatorId', operatorId);
            const safePart = String(item?.partNumber || 'part').replace(/[^a-zA-Z0-9._-]+/g, '_');
            const safePri = pri ? String(pri).replace(/[^a-zA-Z0-9._-]+/g, '_') : '';
            const safeBatch = batch ? String(batch).replace(/[^a-zA-Z0-9._-]+/g, '_') : '';
            const safeExpiry = expiryDate ? String(expiryDate).replace(/[^a-zA-Z0-9._-]+/g, '_') : '';
            const safePo = selectedPo ? String(selectedPo).replace(/[^a-zA-Z0-9._-]+/g, '_') : '';
            const safeSerial = serial
              ? String(serial).replace(/[^a-zA-Z0-9._-]+/g, '_')
              : '';
            const filename = ['Inventory-Part', safePart, safePri || safeBatch || safePo, safeExpiry, safeSerial].filter(Boolean).join('_') + '.pdf';
            await downloadAuthenticatedPdf(
              `${storeApi(`/inventory/${itemId}/part-detail.pdf`)}?${params.toString()}`,
              filename
            );
            downloadCount += 1;
          }
        }
      }
      showToast(
        downloadCount > 1
          ? `${downloadCount} store acceptance tag PDFs downloaded`
          : 'Store acceptance tag PDF downloaded',
        'success'
      );
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Failed to download part detail PDF', 'error');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {item ? (formatPartMasterOptionLabel(item) || 'Inventory item') : 'Item details'}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingItem && !item ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : item ? (
            <>
              <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
                <Tab label="Details" />
                <Tab label="History" />
              </Tabs>

              {tabValue === 0 && (
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Part number</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>{item.partNumber}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Reference part number</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>{item.referencePartNumber || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Store code</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {item.storePartCode || `${item.aircraftRegistration || 'GEN'}__${item.partNumber}`}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">PO number</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {item.purchaseOrderNumber || '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">PRI number(s)</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {priOptions.length > 0 ? priOptions.join(', ') : '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Category</Typography>
                      <Typography variant="body1">
                        <Chip
                          label={INVENTORY_CATEGORY_LABELS[item.category] || item.category}
                          size="small"
                        />
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Store type</Typography>
                      <Typography variant="body1">{formatStoreTypeLabel(item.storeType)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Aircraft scope</Typography>
                      <Typography variant="body1">
                        {item.aircraftScope === 'AIRCRAFT' ? 'Aircraft-specific' : 'GEN (common components)'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Aircraft</Typography>
                      <Typography variant="body1">
                        {item.aircraftScope === 'AIRCRAFT'
                          ? item.aircraftId?.registration || item.aircraftRegistration || '—'
                          : 'GEN'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Condition</Typography>
                      <Typography variant="body1">
                        {PART_CONDITION_LABELS[item.condition] || item.condition || '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Unit metric</Typography>
                      <Typography variant="body1">{unitMetricLabel(item.unitMetric)}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Description</Typography>
                      <Typography variant="body1">{item.description || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Quantity</Typography>
                      <Typography variant="body1">{item.quantity}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Ordered quantity</Typography>
                      <Typography variant="body1">{Number(item.onOrderQuantity) || 0}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      <Typography variant="body1"><Chip label={item.status} size="small" color={item.status === 'IN_INVENTORY' ? 'success' : 'default'} /></Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Location
                      </Typography>
                      {canEditLocation ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Location (free text)"
                            value={locationForm.locationText}
                            onChange={(e) => setLocationForm((prev) => ({
                              ...prev,
                              locationText: e.target.value
                            }))}
                          />
                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                size="small"
                                fullWidth
                                label="Warehouse"
                                value={locationForm.location.warehouse}
                                onChange={(e) => setLocationForm((prev) => ({
                                  ...prev,
                                  location: { ...prev.location, warehouse: e.target.value }
                                }))}
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                size="small"
                                fullWidth
                                label="Shelf"
                                value={locationForm.location.shelf}
                                onChange={(e) => setLocationForm((prev) => ({
                                  ...prev,
                                  location: { ...prev.location, shelf: e.target.value }
                                }))}
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                size="small"
                                fullWidth
                                label="Bin"
                                value={locationForm.location.bin}
                                onChange={(e) => setLocationForm((prev) => ({
                                  ...prev,
                                  location: { ...prev.location, bin: e.target.value }
                                }))}
                              />
                            </Grid>
                          </Grid>
                          <Box>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={savingLocation ? <CircularProgress size={16} color="inherit" /> : <Save />}
                              onClick={handleSaveLocation}
                              disabled={savingLocation}
                            >
                              {savingLocation ? 'Saving…' : 'Save location'}
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body1">
                          {item.locationText || getLocationString(item.location) || '—'}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">
                        {item.category === 'TOOL' ? 'Calibration due' : 'Expiry date'}
                      </Typography>
                      <Typography variant="body1">
                        {displayExpiryDate
                          ? new Date(displayExpiryDate).toLocaleDateString()
                          : (item.category !== 'TOOL' && item.shelfLifeNotApplicable ? 'N/A' : '—')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Supplier</Typography>
                      <Typography variant="body1">{item.supplier ? `${item.supplier.name || ''} ${item.supplier.code ? `(${item.supplier.code})` : ''}`.trim() || '—' : '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Certificate</Typography>
                      <CertificateField
                        certificateNumber={item.certificateNumber}
                        document={certificateDoc}
                        onDownload={certificateDoc ? handleDownloadCertificate : null}
                        downloading={downloadingCertificate}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Batch number</Typography>
                      <Typography variant="body1">{displayBatchNumber || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Serial number{serialDisplay.includes(',') ? 's' : ''}
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: serialDisplay ? 'monospace' : 'inherit' }}>
                        {serialDisplay || '—'}
                      </Typography>
                    </Grid>
                    {Array.isArray(item.unitDetails) && item.unitDetails.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Unit batch / serial details
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Unit</TableCell>
                              <TableCell>Store</TableCell>
                              <TableCell>PRI</TableCell>
                              <TableCell>PO number</TableCell>
                              <TableCell>Condition</TableCell>
                              <TableCell>Batch</TableCell>
                              <TableCell>Serial</TableCell>
                              <TableCell>Expiry</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(Array.isArray(item.unitDetails) ? item.unitDetails : []).map((unit, idx) => (
                              <TableRow key={`unit-${idx}`}>
                                <TableCell>{idx + 1}</TableCell>
                                <TableCell>{formatStoreTypeLabel(unit?.storeType || item.storeType)}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>{unit?.priNumber || '—'}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>{unitPoNumber(unit)}</TableCell>
                                <TableCell>{PART_CONDITION_LABELS[unit?.condition] || unit?.condition || '—'}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>{unit?.batchNumber || '—'}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>{unit?.serialNumber || '—'}</TableCell>
                                <TableCell>
                                  {unit?.expiryDate ? new Date(unit.expiryDate).toLocaleDateString() : '—'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Grid>
                    )}
                    {item.isArchived && (
                      <Grid item xs={12}>
                        <Chip label="Archived" size="small" color="default" variant="outlined" />
                        {item.archivedAt && (
                          <Typography variant="caption" sx={{ ml: 1 }}>{new Date(item.archivedAt).toLocaleString()}</Typography>
                        )}
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Link component="button" variant="body2" onClick={() => setDocumentsOpen(true)}>
                        {canEditLocation ? 'View / manage documents' : 'View documents'}
                      </Link>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Download store acceptance tag (A6 PDF
                        {usePriSelection ? ', by PRI' : (poOptions.length > 0 ? ', by PO' : '')}
                        {serialsForSelection.length > 0
                          ? ' — one tag per serial; leave Serial blank to download all'
                          : (priOptions.length > 1
                            ? ' — one tag per batch/lot PRI; leave PRI blank to download all'
                            : (batchOptionsForTag.length > 1
                              ? ' — one tag per batch/lot'
                              : ' — single tag for batch/lot'))}
                        )
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                        {usePriSelection && (
                          <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel id="inv-detail-pri-label">PRI number</InputLabel>
                            <Select
                              labelId="inv-detail-pri-label"
                              label="PRI number"
                              value={selectedPri}
                              displayEmpty={priOptions.length > 1}
                              onChange={(e) => handlePriChange(e.target.value)}
                            >
                              {priOptions.length > 1 && (
                                <MenuItem value="">
                                  {serialsForSelection.length > 0
                                    ? 'Select PRI'
                                    : 'All batches (separate tags)'}
                                </MenuItem>
                              )}
                              {priOptions.map((pri) => (
                                <MenuItem key={pri} value={pri} sx={{ fontFamily: 'monospace' }}>
                                  {pri}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                        {!usePriSelection && poOptions.length > 0 && (
                          <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel id="inv-detail-po-label">PO number</InputLabel>
                            <Select
                              labelId="inv-detail-po-label"
                              label="PO number"
                              value={selectedPo}
                              displayEmpty={poOptions.length > 1}
                              onChange={(e) => handlePoChange(e.target.value)}
                            >
                              {poOptions.length > 1 && (
                                <MenuItem value="">
                                  Select PO
                                </MenuItem>
                              )}
                              {poOptions.map((po) => (
                                <MenuItem key={po} value={po} sx={{ fontFamily: 'monospace' }}>
                                  {po}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                        {serialsForSelection.length > 1 && (
                          <FormControl
                            size="small"
                            sx={{ minWidth: 200 }}
                            disabled={
                              (usePriSelection && priOptions.length > 1 && !selectedPri)
                              || (!usePriSelection && poOptions.length > 1 && !selectedPo)
                            }
                          >
                            <InputLabel id="inv-detail-serial-label">Serial number</InputLabel>
                            <Select
                              labelId="inv-detail-serial-label"
                              label="Serial number"
                              value={selectedSerial}
                              displayEmpty
                              onChange={(e) => setSelectedSerial(e.target.value)}
                            >
                              <MenuItem value="">
                                All serials (separate tags)
                              </MenuItem>
                              {serialsForSelection.map((serial) => (
                                <MenuItem key={serial} value={serial} sx={{ fontFamily: 'monospace' }}>
                                  {serial}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={downloadingPdf ? <CircularProgress size={16} /> : <PictureAsPdf />}
                          onClick={handleDownloadPartDetailPdf}
                          disabled={
                            downloadingPdf
                            || (usePriSelection && priOptions.length > 1 && !selectedPri && serialsForSelection.length > 0)
                            || (!usePriSelection && poOptions.length > 1 && !selectedPo)
                          }
                        >
                          {downloadingPdf ? 'Generating…' : 'Download PDF'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {tabValue === 1 && (
                <Box>
                  {loadingHistory ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : history.length === 0 ? (
                    <Alert severity="info">No history recorded for this item.</Alert>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Who</TableCell>
                          <TableCell>Source</TableCell>
                          <TableCell>Quantity</TableCell>
                          <TableCell>Link</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {history.map((ev) => (
                          <TableRow key={ev.id}>
                            <TableCell>{ev.date ? new Date(ev.date).toLocaleString() : '—'}</TableCell>
                            <TableCell>{whoName(ev.who)}</TableCell>
                            <TableCell>{ev.source || '—'}</TableCell>
                            <TableCell>
                              {ev.quantityBefore != null || ev.quantityAfter != null ? (
                                <Typography variant="body2">
                                  {ev.quantityBefore != null && `${ev.quantityBefore}`}
                                  {ev.quantityAfter != null && ` → ${ev.quantityAfter}`}
                                  {ev.quantityDelta != null && (
                                    <Typography component="span" variant="caption" color={ev.quantityDelta >= 0 ? 'text.secondary' : 'error'} sx={{ ml: 1 }}>
                                      ({ev.quantityDelta >= 0 ? '+' : ''}{ev.quantityDelta})
                                    </Typography>
                                  )}
                                </Typography>
                              ) : '—'}
                            </TableCell>
                            <TableCell>
                              {ev.sourceId && ev.sourceType ? (
                                <Link
                                  component="button"
                                  variant="body2"
                                  onClick={() => handleOpenRecord(ev.sourceType, ev.sourceId, ev.source)}
                                >
                                  {ev.source} #{String(ev.sourceId).slice(-8)}
                                </Link>
                              ) : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              )}
            </>
          ) : (
            <Alert severity="error">Item not found.</Alert>
          )}
        </DialogContent>
      </Dialog>

      <StoreDocumentsDialog
        open={documentsOpen}
        onClose={() => setDocumentsOpen(false)}
        entityId={itemId}
        entityType="inventory"
        entityLabel={item ? formatPartMasterOptionLabel(item) : ''}
        readOnly={!canEditLocation}
      />
    </>
  );
};

export default InventoryItemDetailModal;
